import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { showSuccess, showError } from '../utils/toastHelper';



const SignUp = () => {
  const { register } = useAuth();
  const { branding, loading: brandingLoading, notFound, error, domain } = useBranding();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [formError, setFormError] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    password: '',
    passwordConfirm: '',
    phoneNumber: ''
  });
  
  const hasMinLength = formData.password.length >= 8;
  const hasUpper = /[A-Z]/.test(formData.password);
  const hasLower = /[a-z]/.test(formData.password);
  const hasNumber = /\d/.test(formData.password);
  const isPasswordValid = hasMinLength && hasUpper && hasLower && hasNumber;

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === 'phoneNumber') {
      let digits = value.replace(/\D/g, '');
      if (digits.startsWith('0')) digits = digits.substring(1);
      if (digits.length > 0 && !digits.startsWith('5')) digits = '5' + digits;
      formattedValue = digits.substring(0, 10);
    }

    setFormData(prev => ({ ...prev, [name]: formattedValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFormError('');

    if (formData.password !== formData.passwordConfirm) {
      setFormError('Şifreler eşleşmiyor');
      setLoading(false);
      return;
    }
    if (!isPasswordValid) {
      setFormError('Lütfen tüm şifre kurallarını karşıladığınızdan emin olun.');
      setLoading(false);
      return;
    }
    const phoneRegex = /^5[0-9]{9}$/;
    if (!phoneRegex.test(formData.phoneNumber)) {
      setFormError('Geçerli bir telefon numarası giriniz (örn: 5XX XXX XX XX)');
      setLoading(false);
      return;
    }

    try {
      // Localde Origin header her zaman localhost olur; backend doğru tenant'ı
      // bulabilsin diye BrandingContext'in çözdüğü domain'i body'ye geçiriyoruz.
      const result = await register({ ...formData, tenantDomain: domain });
      if (result.success) {
        showSuccess(result.message || 'Doğrulama bağlantısını e-posta adresinize gönderdik. Lütfen gelen kutunuzu (ve spam klasörünü) kontrol edip hesabınızı doğrulayın.');
      } else {
        setFormError(result.message || 'Kayıt işlemi başarısız');
      }
    } catch (error) {
      setFormError(error.message || 'Kayıt olurken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  // Derived branding values
  const companyName = branding.name || '';
  const logoSrc = branding.logoUrl || branding.companyLogo || null;
  const accent = branding.brandColor || '#1e293b';
  const accentSoft = accent + '18';
  const heroTitle = branding.heroTitle || '';
  const heroSub = branding.heroSubtitle || branding.tagline || '';

  if (brandingLoading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: '#f8f9fc' }}>
        <div className="spinner-border" style={{ color: '#aaa' }} role="status" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: '#f8f9fc' }}>
        <div className="text-center px-4" style={{ maxWidth: 420 }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>⚠️</div>
          <h2 className="fw-bold mb-2" style={{ color: '#1a1a2e' }}>Bağlantı Hatası</h2>
          <p className="text-muted mb-3">
            Sunucuya bağlanılamadı. Lütfen daha sonra tekrar deneyin.
          </p>
          {error && (
            <p className="text-muted mb-3" style={{ fontSize: 12 }}>
              Detay: {error}
            </p>
          )}
          <button className="btn btn-outline-secondary btn-sm" onClick={() => window.location.reload()}>
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: '#f8f9fc' }}>
        <div className="text-center px-4" style={{ maxWidth: 420 }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>🔍</div>
          <h2 className="fw-bold mb-2" style={{ color: '#1a1a2e' }}>Şirket Bulunamadı</h2>
          <p className="text-muted mb-0">
            Bu adrese kayıtlı bir şirket bulunmuyor.<br />
            Lütfen URL'yi kontrol edin veya yöneticinizle iletişime geçin.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      {/* ── Left: Branded Panel ─────────────────────────────── */}
      <div className="login-brand-panel" style={{ '--accent': accent, '--accent-soft': accentSoft }}>
        <div className="login-brand-bg" />
        <div className="login-brand-content">
          {logoSrc && (
            <img src={logoSrc} alt={companyName || 'Logo'} className="login-brand-logo" />
          )}
          {heroTitle && <h1 className="login-brand-title">{heroTitle}</h1>}
          {heroSub && <p className="login-brand-subtitle">{heroSub}</p>}
          {!logoSrc && !heroTitle && companyName && (
            <h1 className="login-brand-title">{companyName}</h1>
          )}
        </div>
      </div>

      {/* ── Right: Form ─────────────────────────────────────── */}
      <div className="login-form-panel">
        <div className="login-form-wrapper" style={{ maxWidth: '540px' }}>
          {/* Mobile-only header */}
          <div className="login-mobile-header" style={{ '--accent': accent }}>
            {logoSrc ? (
              <img src={logoSrc} alt={companyName || 'Logo'} className="login-mobile-logo" />
            ) : companyName ? (
              <span className="login-mobile-name" style={{ color: accent }}>{companyName}</span>
            ) : null}
          </div>

          <div className="login-form-inner">
            <h2 className="login-form-title">Kayıt Olun</h2>
            <p className="login-form-desc">Sisteme katılmak için bilgilerinizi eksiksiz girin.</p>

            {formError && (
              <div className="alert alert-danger d-flex align-items-center mb-4" role="alert" style={{ fontSize: '0.875rem', borderRadius: '10px', backgroundColor: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }}>
                <i className="fas fa-exclamation-circle me-2" style={{ fontSize: '1.2rem' }} />
                <div>{formError}</div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="signup-form-grid">
              <div className="signup-field-half">
                <label className="login-label" htmlFor="signup-name">Ad</label>
                <div className="login-input-wrap" style={{ '--accent': accent }}>
                  <i className="fas fa-user login-input-icon" />
                  <input
                    id="signup-name"
                    type="text"
                    name="name"
                    className="login-input"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    placeholder="Adınız"
                  />
                </div>
              </div>

              <div className="signup-field-half">
                <label className="login-label" htmlFor="signup-surname">Soyad</label>
                <div className="login-input-wrap" style={{ '--accent': accent }}>
                  <i className="fas fa-user login-input-icon" />
                  <input
                    id="signup-surname"
                    type="text"
                    name="surname"
                    className="login-input"
                    value={formData.surname}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    placeholder="Soyadınız"
                  />
                </div>
              </div>

              <div className="signup-field-half">
                <label className="login-label" htmlFor="signup-email">E-posta</label>
                <div className="login-input-wrap" style={{ '--accent': accent }}>
                  <i className="fas fa-envelope login-input-icon" />
                  <input
                    id="signup-email"
                    type="email"
                    name="email"
                    className="login-input"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    placeholder="ornek@mail.com"
                  />
                </div>
              </div>

              <div className="signup-field-half">
                <label className="login-label" htmlFor="signup-phone">Telefon</label>
                <div className="login-input-wrap" style={{ '--accent': accent }}>
                  <i className="fas fa-phone login-input-icon" />
                  <input
                    id="signup-phone"
                    type="tel"
                    name="phoneNumber"
                    className="login-input"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    placeholder="5XX XXX XX XX"
                  />
                </div>
              </div>

              <div className="signup-field-half">
                <label className="login-label" htmlFor="signup-password">Şifre</label>
                <div className="login-input-wrap" style={{ '--accent': accent }}>
                  <i className="fas fa-lock login-input-icon" />
                  <input
                    id="signup-password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    className="login-input"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    placeholder="••••••••"
                    minLength="8"
                  />
                  <button
                    type="button"
                    className="login-eye-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    <i className={`fas fa-${showPassword ? 'eye-slash' : 'eye'}`} />
                  </button>
                </div>
                
                {/* Password Hints */}
                <div className="mt-2" style={{ fontSize: '0.75rem' }}>
                  <div className={`d-flex align-items-center ${hasMinLength ? 'text-success' : 'text-muted'}`}>
                    <i className={`fas ${hasMinLength ? 'fa-check-circle' : 'fa-circle'} me-1`} style={{ fontSize: '0.6rem' }} /> En az 8 karakter
                  </div>
                  <div className={`d-flex align-items-center ${hasUpper ? 'text-success' : 'text-muted'}`}>
                    <i className={`fas ${hasUpper ? 'fa-check-circle' : 'fa-circle'} me-1`} style={{ fontSize: '0.6rem' }} /> En az 1 büyük harf
                  </div>
                  <div className={`d-flex align-items-center ${hasLower ? 'text-success' : 'text-muted'}`}>
                    <i className={`fas ${hasLower ? 'fa-check-circle' : 'fa-circle'} me-1`} style={{ fontSize: '0.6rem' }} /> En az 1 küçük harf
                  </div>
                  <div className={`d-flex align-items-center ${hasNumber ? 'text-success' : 'text-muted'}`}>
                    <i className={`fas ${hasNumber ? 'fa-check-circle' : 'fa-circle'} me-1`} style={{ fontSize: '0.6rem' }} /> En az 1 rakam
                  </div>
                </div>
              </div>

              <div className="signup-field-half">
                <label className="login-label" htmlFor="signup-password-confirm">Şifre Tekrar</label>
                <div className="login-input-wrap" style={{ '--accent': accent }}>
                  <i className="fas fa-lock login-input-icon" />
                  <input
                    id="signup-password-confirm"
                    type={showPasswordConfirm ? 'text' : 'password'}
                    name="passwordConfirm"
                    className="login-input"
                    value={formData.passwordConfirm}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    placeholder="••••••••"
                    minLength="8"
                  />
                  <button
                    type="button"
                    className="login-eye-btn"
                    onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                    tabIndex={-1}
                  >
                    <i className={`fas fa-${showPasswordConfirm ? 'eye-slash' : 'eye'}`} />
                  </button>
                </div>
              </div>

              <div className="signup-field-full mt-2">
                <button
                  type="submit"
                  className="login-submit w-100"
                  style={{ '--accent': accent }}
                  disabled={loading}
                >
                  {loading ? (
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  ) : (
                    <i className="fas fa-user-plus me-2" />
                  )}
                  Hesap Oluştur
                </button>
              </div>
            </form>

            <p className="login-signup-text">
              Zaten hesabınız var mı?{' '}
              <Link to="/login" className="login-link fw-semibold" style={{ color: accent }}>Giriş Yapın</Link>
            </p>
          </div>
        </div>
      </div>

      <style>{`
        /* ── Layout ────────────────────────────────────── */
        .login-page {
          display: flex;
          min-height: 100vh;
          background: #f8f9fc;
        }

        /* ── Brand Panel (left, desktop) ───────────────── */
        .login-brand-panel {
          position: relative;
          width: 45%;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        .login-brand-bg {
          position: absolute;
          inset: 0;
          background: linear-gradient(160deg, var(--accent) 0%, color-mix(in srgb, var(--accent) 70%, #000) 100%);
        }
        .login-brand-bg::before {
          content: '';
          position: absolute;
          inset: 0;
          background:
            radial-gradient(circle at 20% 80%, rgba(255,255,255,0.08) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255,255,255,0.06) 0%, transparent 40%);
        }
        .login-brand-content {
          position: relative;
          z-index: 1;
          text-align: center;
          padding: 3rem 2.5rem;
          max-width: 440px;
          animation: brandFadeIn 0.6s ease-out 0.1s both;
        }
        .login-brand-logo {
          height: 180px;
          max-width: 360px;
          object-fit: contain;
          margin-bottom: 2rem;
          /* filter: brightness(0) invert(1); */
        }
        .login-brand-title {
          color: #fff;
          font-size: 1.75rem;
          font-weight: 700;
          letter-spacing: -0.5px;
          margin-bottom: 0.75rem;
          line-height: 1.3;
          text-shadow: 0 2px 12px rgba(0,0,0,0.15);
        }
        .login-brand-subtitle {
          color: rgba(255,255,255,0.8);
          font-size: 1rem;
          line-height: 1.6;
          margin: 0;
        }

        /* ── Form Panel (right) ────────────────────────── */
        .login-form-panel {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
        }
        .login-form-wrapper {
          width: 100%;
          animation: formSlideUp 0.5s ease-out 0.15s both;
        }
        .login-form-inner {
          background: #fff;
          border-radius: 16px;
          padding: 2.5rem 2rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06);
        }
        .login-form-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1a1a2e;
          margin-bottom: 0.25rem;
        }
        .login-form-desc {
          color: #6b7280;
          font-size: 0.9rem;
          margin-bottom: 1.75rem;
        }

        /* ── Mobile header ─────────────────────────────── */
        .login-mobile-header {
          display: none;
          text-align: center;
          margin-bottom: 1.5rem;
        }
        .login-mobile-logo {
          height: 48px;
          object-fit: contain;
        }
        .login-mobile-name {
          font-size: 1.25rem;
          font-weight: 700;
          letter-spacing: -0.5px;
        }

        /* ── Fields & Grid ─────────────────────────────── */
        .signup-form-grid { 
          display: flex; 
          flex-wrap: wrap; 
          margin: 0 -0.5rem; 
          gap: 1rem 0; 
        }
        .signup-field-half { 
          padding: 0 0.5rem; 
          width: 50%; 
          display: flex; 
          flex-direction: column; 
          gap: 0.375rem; 
        }
        .signup-field-full { 
          padding: 0 0.5rem; 
          width: 100%; 
          display: flex; 
          flex-direction: column; 
          gap: 0.375rem; 
        }

        .login-label {
          font-size: 0.8125rem;
          font-weight: 600;
          color: #374151;
        }
        .login-input-wrap {
          display: flex;
          align-items: center;
          border: 1.5px solid #e5e7eb;
          border-radius: 10px;
          background: #fff;
          transition: border-color 0.2s, box-shadow 0.2s;
          overflow: hidden;
        }
        .login-input-wrap:focus-within {
          border-color: var(--accent);
          box-shadow: 0 0 0 3px var(--accent-soft, rgba(79,70,229,0.1));
        }
        .login-input-icon {
          padding: 0 0 0 0.875rem;
          font-size: 0.875rem;
          color: #9ca3af;
          flex-shrink: 0;
          transition: color 0.2s;
        }
        .login-input-wrap:focus-within .login-input-icon { color: var(--accent); }
        .login-input {
          flex: 1;
          border: none;
          outline: none;
          padding: 0.75rem;
          font-size: 0.9375rem;
          color: #1a1a2e;
          background: transparent;
          min-width: 0;
        }
        .login-input::placeholder { color: #c4c8d0; }
        .login-input:-webkit-autofill,
        .login-input:-webkit-autofill:hover, 
        .login-input:-webkit-autofill:focus, 
        .login-input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0px 1000px white inset !important;
          -webkit-text-fill-color: #1a1a2e !important;
          transition: background-color 5000s ease-in-out 0s;
        }
        .login-eye-btn {
          background: none;
          border: none;
          padding: 0 0.875rem;
          color: #9ca3af;
          cursor: pointer;
          transition: color 0.2s;
          font-size: 0.9rem;
        }
        .login-eye-btn:hover { color: #6b7280; }

        /* ── Submit ────────────────────────────────────── */
        .login-submit {
          margin-top: 0.5rem;
          padding: 0.8rem 1.5rem;
          border: none;
          border-radius: 10px;
          font-size: 0.9375rem;
          font-weight: 600;
          color: #fff;
          background: var(--accent);
          cursor: pointer;
          transition: transform 0.15s, box-shadow 0.2s, opacity 0.2s;
        }
        .login-submit:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 14px color-mix(in srgb, var(--accent) 40%, transparent);
        }
        .login-submit:active:not(:disabled) { transform: translateY(0); }
        .login-submit:disabled { opacity: 0.65; cursor: not-allowed; }

        /* ── Links ─────────────────────────────────────── */
        .login-link {
          font-size: 0.8125rem;
          text-decoration: none;
          transition: opacity 0.2s;
        }
        .login-link:hover { opacity: 0.75; }
        .login-signup-text {
          text-align: center;
          margin: 1.5rem 0 0;
          font-size: 0.875rem;
          color: #6b7280;
        }

        /* ── Animations ────────────────────────────────── */
        @keyframes brandFadeIn {
          from { opacity: 0; transform: scale(0.96); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes formSlideUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* ── Responsive ────────────────────────────────── */
        @media (max-width: 991.98px) {
          .login-brand-panel { display: none; }
          .login-form-panel { padding: 1.5rem 1rem; }
          .login-mobile-header { display: block; }
          .login-form-inner { padding: 2rem 1.5rem; }
        }
        @media (max-width: 575.98px) {
          .login-form-inner {
            box-shadow: none;
            border-radius: 12px;
            padding: 1.75rem 1.25rem;
          }
          .login-form-title { font-size: 1.35rem; }
          .signup-field-half { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default SignUp;